import { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { graphql, createFragmentContainer } from 'react-relay/legacy';
import { FormattedMessage } from 'dibs-react-intl';
import classNames from 'classnames';

import ZoomIn from 'dibs-icons/exports/legacy/ZoomIn';
import ZoomOut from 'dibs-icons/exports/legacy/ZoomOut';
import { Tooltip } from 'dibs-elements/exports/Tooltip';
import Download from 'dibs-icons/exports/legacy/Download';
import GridEmpty from 'dibs-icons/exports/legacy/GridEmpty';
import Share from 'dibs-icons/exports/legacy/Share';
import * as tracking from 'dibs-tracking';

import { DESKTOP } from '../../../utils/deviceType';
import {
    loadTableContents as loadTableContentsDispatch,
    togglePageOverview as togglePageOverviewDispatch,
    toggleShareOptions as toggleShareOptionsDispatch,
    zoomIn as zoomInDispatch,
    zoomOut as zoomOutDispatch,
} from '../../actionCreators/uiActionCreators';
import { isMobilePortrait } from '../../catalogHelpers';
import styles from '../../styles/catalogMenuBar.scss';
import { CatalogPageNumber } from '../CatalogPageNumber';
import { ShareOptionsPanel } from './ShareOptionsPanel';
import { noop } from '../../catalogConstants';
import { Link } from 'dibs-elements/exports/Link';

const hoverableIcons = {
    DOWNLOAD: 'download',
    OVERVIEW: 'overview',
    SHARE: 'share',
    ZOOM: 'zoom',
};

export class CatalogMenuBarComponent extends Component {
    constructor(props) {
        super(props);

        this.state = {
            iconHovered: null,
        };

        this.onMouseEnterToolTip = this.onMouseEnterToolTip.bind(this);
        this.onMouseLeaveToolTip = this.onMouseLeaveToolTip.bind(this);
        this.getCatalogTitleTracking = this.getCatalogTitleTracking.bind(this);
        this.buildZoomButton = this.buildZoomButton.bind(this);
        this.buildPreviewsButton = this.buildPreviewsButton.bind(this);
        this.buildDownloadButton = this.buildDownloadButton.bind(this);
        this.buildShareButton = this.buildShareButton.bind(this);
    }

    onMouseEnterToolTip(iconType) {
        this.setState(() => ({ iconHovered: iconType }));
    }

    onMouseLeaveToolTip() {
        this.setState(() => ({ iconHovered: null }));
    }

    getCatalogTitleTracking() {
        return this.props.catalogPage?.title || 'n/a';
    }

    buildZoomButton() {
        const { catalogPage, zoomedIn, zoomIn, zoomOut } = this.props;
        const singlePageDimensions = catalogPage?.images?.singlePageDimensions;
        const pageCount = catalogPage?.images?.pageCount;

        return (
            <div className={styles.iconSection}>
                <Link
                    className={styles.iconWrapper}
                    dataTn="zoomWrapper"
                    onClick={() => {
                        if (zoomedIn) {
                            zoomOut();
                        } else {
                            zoomIn(singlePageDimensions, pageCount);
                            tracking.trackEvent({
                                category: 'product catalog',
                                action: 'image magnifier clicked',
                                label: `${this.getCatalogTitleTracking()}`,
                            });
                        }
                    }}
                    onMouseEnter={() => {
                        this.onMouseEnterToolTip(hoverableIcons.ZOOM);
                    }}
                    onMouseLeave={this.onMouseLeaveToolTip}
                    onMouseUp={this.onMouseLeaveToolTip}
                >
                    {zoomedIn ? (
                        <ZoomOut className={classNames(styles.icon, styles.zoom)} />
                    ) : (
                        <ZoomIn className={classNames(styles.icon, styles.zoom)} />
                    )}
                    <Tooltip
                        isVisible={this.state.iconHovered === hoverableIcons.ZOOM}
                        direction="right"
                        type="small"
                    >
                        <div className={styles.textAlignCenter}>
                            {zoomedIn ? (
                                <FormattedMessage
                                    id="CatalogMenuBar.zoomOut"
                                    defaultMessage="Zoom Out"
                                />
                            ) : (
                                <FormattedMessage
                                    id="CatalogMenuBar.zoomIn"
                                    defaultMessage="Zoom In"
                                />
                            )}
                        </div>
                    </Tooltip>
                </Link>
            </div>
        );
    }

    buildPreviewsButton() {
        const {
            canLoadTableContents,
            deviceType,
            loadTableContents,
            pageOverviewOpen,
            screenOrientation,
            togglePageOverview,
        } = this.props;

        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);
        const showTooltip = deviceType === DESKTOP;

        return (
            <div
                className={classNames({
                    [styles.iconSection]: true,
                    [styles.panelOpen]: pageOverviewOpen,
                })}
            >
                <Link
                    className={classNames({
                        [styles.iconWrapper]: true,
                        [styles.iconWrapperMobilePortrait]: isPhonePortrait,
                        gridIconWrapper: true,
                    })}
                    dataTn="gridWrapper"
                    onMouseEnter={
                        showTooltip
                            ? () => {
                                  this.onMouseEnterToolTip(hoverableIcons.OVERVIEW);
                              }
                            : noop
                    }
                    onMouseLeave={showTooltip ? this.onMouseLeaveToolTip : noop}
                    onMouseUp={showTooltip ? this.onMouseLeaveToolTip : noop}
                    onClick={() => {
                        // we are about to open overview, track event
                        if (!pageOverviewOpen) {
                            tracking.trackEvent({
                                category: 'product catalog',
                                action: 'thumbnail gallery clicked',
                                label: `${this.getCatalogTitleTracking()}`,
                            });
                        }

                        togglePageOverview();

                        // only load table of contents if actually requested
                        if (!canLoadTableContents) {
                            loadTableContents();
                        }
                    }}
                >
                    <GridEmpty className={classNames(styles.icon, styles.grid)} />
                    {showTooltip && (
                        <Tooltip
                            isVisible={
                                this.state.iconHovered === hoverableIcons.OVERVIEW &&
                                !pageOverviewOpen
                            }
                            direction="right"
                            type="small"
                        >
                            <div className={styles.textAlignCenter}>
                                <FormattedMessage
                                    id="CatalogMenuBar.viewPages"
                                    defaultMessage="View Pages"
                                />
                            </div>
                        </Tooltip>
                    )}
                </Link>
            </div>
        );
    }

    buildDownloadButton() {
        const { catalogPage } = this.props;

        let filePath = catalogPage?.embeddedFile?.filePath;
        if (!filePath) {
            filePath = catalogPage?.file?.filePath;
        }

        return (
            <div className={styles.iconSection}>
                <Link
                    href={filePath}
                    className={styles.downloadLink}
                    onClick={() => {
                        tracking.trackEvent({
                            category: 'product catalog',
                            action: 'download catalog clicked',
                            label: `${this.getCatalogTitleTracking()}`,
                        });
                    }}
                >
                    <Link
                        className={styles.iconWrapper}
                        datatn={'downloadWrapper'}
                        onMouseEnter={() => {
                            this.onMouseEnterToolTip(hoverableIcons.DOWNLOAD);
                        }}
                        onMouseLeave={this.onMouseLeaveToolTip}
                        onMouseUp={this.onMouseLeaveToolTip}
                    >
                        <Download className={styles.icon} />
                        <Tooltip
                            isVisible={this.state.iconHovered === hoverableIcons.DOWNLOAD}
                            direction="right"
                            type="small"
                        >
                            <div className={styles.textAlignCenter}>
                                <FormattedMessage
                                    id="CatalogMenuBar.downloadPDF"
                                    defaultMessage="Download PDF"
                                />
                            </div>
                        </Tooltip>
                    </Link>
                </Link>
            </div>
        );
    }

    buildShareButton() {
        const { deviceType, screenOrientation, shareOptionsOpen, toggleShareOptions } = this.props;
        const showTooltip = deviceType === DESKTOP;

        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);

        return (
            <div
                className={classNames({
                    [styles.iconSection]: true,
                    [styles.panelOpen]: shareOptionsOpen,
                    [styles.shareWrapperMobilePortrait]: isPhonePortrait,
                })}
            >
                <Link
                    className={classNames({
                        [styles.iconWrapper]: true,
                        [styles.iconWrapperMobilePortrait]: isPhonePortrait,
                        shareIconWrapper: true,
                    })}
                    datatn={'shareWrapper'}
                    onMouseEnter={() => {
                        if (!shareOptionsOpen) {
                            this.onMouseEnterToolTip(hoverableIcons.SHARE);
                        }
                    }}
                    onMouseLeave={this.onMouseLeaveToolTip}
                    onMouseUp={this.onMouseLeaveToolTip}
                    onClick={() => {
                        toggleShareOptions();
                    }}
                >
                    <Share className={styles.icon} />
                    {showTooltip && (
                        <Tooltip
                            isVisible={this.state.iconHovered === hoverableIcons.SHARE}
                            direction={'right'}
                            type={'small'}
                        >
                            <div className={styles.textAlignCenter}>
                                <FormattedMessage
                                    id="CatalogMenuBar.shareCatalog"
                                    defaultMessage={'Share Catalog'}
                                />
                            </div>
                        </Tooltip>
                    )}
                </Link>
            </div>
        );
    }

    render() {
        const { catalogPage, catalogShareImgURL, deviceType, screenOrientation, shareOptionsOpen } =
            this.props;

        const isPhonePortrait = isMobilePortrait(deviceType, screenOrientation);

        const displayZoom = deviceType === DESKTOP;
        const displayDownload = deviceType === DESKTOP;

        return (
            <div
                className={classNames({
                    [styles.menuBar]: true,
                    [styles.menuMobilePortrait]: isPhonePortrait,
                    [styles.menuNonMobilePortrait]: !isPhonePortrait,
                })}
            >
                {isPhonePortrait && (
                    <CatalogPageNumber catalogPage={catalogPage} deviceType={deviceType} />
                )}
                {displayZoom && this.buildZoomButton()}
                {this.buildPreviewsButton()}
                {displayDownload && this.buildDownloadButton()}
                {this.buildShareButton()}
                {shareOptionsOpen && (
                    <ShareOptionsPanel
                        catalogPage={catalogPage}
                        catalogShareImgURL={catalogShareImgURL}
                        dataTn={'share-options-panel'}
                        deviceType={deviceType}
                    />
                )}
            </div>
        );
    }
}

CatalogMenuBarComponent.propTypes = {
    // component props
    catalogPage: PropTypes.object,
    catalogShareImgURL: PropTypes.string,
    deviceType: PropTypes.string,
    // redux state
    canLoadTableContents: PropTypes.bool,
    pageOverviewOpen: PropTypes.bool,
    screenOrientation: PropTypes.string,
    shareOptionsOpen: PropTypes.bool,
    zoomedIn: PropTypes.bool,
    // redux actions
    loadTableContents: PropTypes.func,
    togglePageOverview: PropTypes.func,
    toggleShareOptions: PropTypes.func,
    zoomIn: PropTypes.func,
    zoomOut: PropTypes.func,
};

CatalogMenuBarComponent.defaultProps = {
    catalogShareImgURL: '',
    deviceType: DESKTOP,
};

const mapStateToProps = state => {
    return {
        canLoadTableContents: state.ui.canLoadTableContents,
        pageOverviewOpen: state.ui.pageOverviewOpen,
        screenOrientation: state.ui.screenOrientation,
        shareOptionsOpen: state.ui.shareOptionsOpen,
        zoomedIn: state.ui.zoomedIn,
    };
};

export const mapDispatchToProps = dispatch => {
    return {
        loadTableContents: () => dispatch(loadTableContentsDispatch()),
        togglePageOverview: () => dispatch(togglePageOverviewDispatch()),
        toggleShareOptions: () => dispatch(toggleShareOptionsDispatch()),
        zoomIn: (singlePageDimensions, pageCount) =>
            dispatch(zoomInDispatch(singlePageDimensions, pageCount)),
        zoomOut: () => dispatch(zoomOutDispatch()),
    };
};

export const CatalogMenuBar = createFragmentContainer(
    connect(mapStateToProps, mapDispatchToProps)(CatalogMenuBarComponent),
    {
        catalogPage: graphql`
            fragment CatalogMenuBar_catalogPage on CatalogPage {
                file {
                    filePath
                }
                embeddedFile {
                    filePath
                }
                images {
                    pageCount
                    singlePageDimensions {
                        height
                        width
                    }
                }
                title
                ...CatalogPageNumber_catalogPage
                ...ShareOptionsPanel_catalogPage
            }
        `,
    }
);
